<script lang="ts">
  import { defineComponent } from 'vue';
  import { StationNotification } from '@/helpers/types';
  import { getMobileInsetsData, sendErrorToSentry } from '@/helpers/utils';
  import Mixins from '@/helpers/mixins';
  import NewReplyNotification from '@/components/ui/notifications/NewReplyNotification.vue';
  import constants from '@/helpers/constants';
  import OrdersFilteredNotification from '@/components/ui/notifications/OrdersFilteredNotification.vue';
  import OrderDelayedNotification from '@/components/ui/notifications/OrderDelayedNotification.vue';
  import { StationNotificationTypes } from '@/helpers/enums';
  import SurveyNotification from '@/components/ui/notifications/SurveyNotification.vue';
  import OrdersDelayedNotification from '@/components/ui/notifications/OrdersDelayedNotification.vue';
  import OrderNotification from '@/components/ui/notifications/OrderNotification.vue';
  import OrderRescheduledNotification from '@/components/ui/notifications/OrderRescheduledNotification.vue';

  export default defineComponent({
    components: {
      OrderRescheduledNotification,
      OrderNotification,
      OrdersDelayedNotification,
      SurveyNotification,
      OrdersFilteredNotification,
      NewReplyNotification,
      OrderDelayedNotification,
    },
    mixins: [Mixins],
    props: {
      notification: {
        type: Object as () => StationNotification,
        required: true,
      },
    },
    emits: {
      hide: (id: string) => true,
    },
    data() {
      return {
        topInset: 0 as number,
        timeoutId: null as ReturnType<typeof setTimeout> | null,
      };
    },
    computed: {
      notificationIcon(): string | null {
        if (
          this.notification.type ===
          StationNotificationTypes.order_filter_applied
        ) {
          return null;
        }

        try {
          return this.imgUrl(
            `icons/notifications/${this.notification.type}.svg`
          );
        } catch (e) {
          sendErrorToSentry('notificationIcon', {
            message: `icons/notifications/${this.notification.type}.svg`,
          });
          console.error(e);
          return null;
        }
      },
      notificationTitle(): string {
        if (this.notification.title) {
          return this.notification.title;
        }

        if (this.$te(`notifications.titles.${this.notification.type}`)) {
          return this.$t(`notifications.titles.${this.notification.type}`);
        }
        return this.$t('titles.new_notification');
      },
    },
    created() {
      const insets = getMobileInsetsData();
      if (insets && insets.top) {
        this.topInset = insets.top;
      }

      const timeout =
        this.notification.timeout || constants.defaultNotificationDuration;

      if (timeout > 0) {
        this.timeoutId = setTimeout(() => {
          this.hideNotification();
        }, timeout);
      }
    },
    beforeUnmount() {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
    },
    methods: {
      hideNotification(): void {
        this.$emit('hide', this.notification.id);
      },
    },
  });
</script>

<template>
  <div
    v-touch:swipe.up="hideNotification"
    :style="{ paddingTop: `${topInset}px` }"
    class="notification-wrapper"
  >
    <div class="py-8">
      <div class="d-flex align-items-center justify-content-between px-8">
        <div class="d-flex align-items-center gap-12">
          <img
            v-if="notificationIcon"
            :alt="
              $t('common.img_alts.notification_icon', {
                title: notificationTitle,
              })
            "
            :src="notificationIcon"
            height="20"
            width="20"
          />
          <h4 class="fw-semibold">{{ notificationTitle }}</h4>
        </div>
        <img
          :alt="$t('common.img_alts.close_notification')"
          :src="imgUrl('icons/close_x_black.svg')"
          height="24"
          width="24"
          @click="hideNotification"
        />
      </div>
      <hr class="dashed my-16 mx-8" />
      <div class="px-8">
        <template
          v-if="notification.type === $enums.StationNotificationTypes.order"
        >
          <OrderNotification
            :data="notification.data"
            @close="hideNotification"
          />
        </template>
        <template
          v-if="notification.type === $enums.StationNotificationTypes.new_reply"
        >
          <NewReplyNotification
            :data="notification.data"
            @close="hideNotification"
          />
        </template>
        <template
          v-if="
            notification.type ===
            $enums.StationNotificationTypes.order_rescheduled
          "
        >
          <OrderRescheduledNotification
            :data="notification.data"
            @close="hideNotification"
          />
        </template>
        <template
          v-if="
            notification.type === $enums.StationNotificationTypes.order_delayed
          "
        >
          <OrderDelayedNotification
            :data="notification.data"
            @close="hideNotification"
          />
        </template>
        <template
          v-if="
            notification.type === $enums.StationNotificationTypes.orders_delayed
          "
        >
          <OrdersDelayedNotification
            :data="notification.data"
            @close="hideNotification"
          />
        </template>
        <template
          v-if="
            notification.type ===
            $enums.StationNotificationTypes.survey_notification
          "
        >
          <SurveyNotification
            :data="notification.data"
            @close="hideNotification"
          />
        </template>
        <template
          v-if="
            notification.type ===
            $enums.StationNotificationTypes.order_filter_applied
          "
        >
          <OrdersFilteredNotification
            :order-list-preselect="notification.data"
            @close="hideNotification"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .notification-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 1000000;
    background-color: white;
    box-shadow: 0px 0px 26px rgba(46, 46, 58, 0.25);
    padding-left: 2 * $base-unit;
    padding-right: 2 * $base-unit;
    padding-bottom: 2 * $base-unit;
    border-bottom-left-radius: 2.5 * $base-unit;
    border-bottom-right-radius: 2.5 * $base-unit;

    /* Transition support */
    transition: all 0.5s ease;
  }
</style>
